import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Swal from "sweetalert2";
import "./ViewBookingLayer.css";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Default marker icon fix (Leaflet doesn't load images properly by default in CRA)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const ViewBookingLayer = () => {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const handlePreviousImage = (e) => {
    e.stopPropagation();

    if (selectedImageIndex > 0) {
      setSelectedImageIndex((prev) => prev - 1);
    }
  };

  const handleNextImage = (e) => {
    e.stopPropagation();

    if (
      booking?.gallery_urls?.length &&
      selectedImageIndex < booking.gallery_urls.length - 1
    ) {
      setSelectedImageIndex((prev) => prev + 1);
    }
  };
  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/booking/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const json = await response.json();

        if (response.ok) {
          setBooking(json.data);
        } else {
          Swal.fire({
            title: "Error",
            text: "Failed to fetch booking details",
            icon: "error",
          });
          navigate("/bookings");
        }
      } catch (error) {
        console.error("Error fetching booking:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch booking details",
          icon: "error",
        });
        navigate("/bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id, token, navigate]);

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "pending":
        return { backgroundColor: "#FFE08A", color: "#664D03" };
      case "priced":
        return { backgroundColor: "#DCC7FF", color: "#55318A" };
      case "active":
        return { backgroundColor: "#B9F2CE", color: "#176638" };
      case "declined":
      case "rejected":
        return { backgroundColor: "#FFC4CC", color: "#9F1239" };
      case "cancelled":
        return { backgroundColor: "#E2E5E9", color: "#454B54" };
      case "completed":
        return { backgroundColor: "#B9ECFF", color: "#075985" };
      default:
        return { backgroundColor: "#E2E5E9", color: "#454B54" };
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="alert alert-danger" role="alert">
        Booking not found.
      </div>
    );
  }

  return (
    <div
      className="booking-details-grid"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "16px",
      }}
    >
      <div
        style={{
          flex: "1.7",
          minWidth: 0,
          border: "none",
          outline: "none",
          boxShadow: "none",
        }}
      >
        <div className="card h-100 p-0 radius-12 my-3">
          <div className="card-header bg-base py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="text-lg fw-semibold mb-0">Booking Information</h6>

              <span
                style={getStatusBadgeStyle(booking.status)}
                className="badge"
              >
                {booking.status?.charAt(0).toUpperCase() +
                  booking.status?.slice(1)}
              </span>
            </div>
          </div>

          <div className="card-body p-24">
            <div className="row g-3">
              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Booking ID
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  #{booking.id}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Booking Date
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.booking_date
                    ? format(new Date(booking.booking_date), "dd MMM, yyyy")
                    : "Not confirmed yet"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Booking Time
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.booking_time || "Not confirmed yet"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Date Created
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.createdAt
                    ? format(
                        new Date(booking.createdAt),
                        "dd MMM, yyyy 'at' hh:mm a",
                      )
                    : "Not available"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Last Updated
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.updatedAt
                    ? format(
                        new Date(booking.updatedAt),
                        "dd MMM, yyyy 'at' hh:mm a",
                      )
                    : "Never updated"}
                </p>
              </div>

              {/* <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Diagnosis Fee
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.diagnosis_fee && "$"}
                  {booking.diagnosis_fee || "-"}
                </p>
              </div> */}
              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Description
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.description || "No description provided"}
                </p>
              </div>

              <div className="col-12">
                <label
                  className="form-label mb-2 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Requested Services
                </label>

                {booking.needs_emergency_service ? (
                  <div>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "#FFC4CC",
                        color: "#9F1239",
                      }}
                    >
                      Emergency / Towing Assistance
                    </span>
                  </div>
                ) : booking.requested_services &&
                  booking.requested_services.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {booking.requested_services.map((service, index) => (
                      <span
                        key={index}
                        className="badge"
                        style={{
                          backgroundColor: "#CFE2FF",
                          color: "#084298",
                          fontSize: "12px",
                        }}
                      >
                        {typeof service === "string" ? service : service.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    No services requested
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {(booking.requested_slots?.length > 0 ||
          booking.agreed_services ||
          booking.garage_offered_slots?.length > 0) && (
          <div className="card h-100 p-0 radius-12 my-3">
            <div className="card-header bg-base py-3">
              <h6 className="text-lg fw-semibold mb-0">Pricing & Scheduling</h6>
            </div>
            <div className="card-body p-24">
              {booking.requested_slots?.length > 0 && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Requested Slots (Car Owner's Window)
                  </label>
                  <div className="d-flex flex-column gap-1">
                    {booking.requested_slots.map((slot, index) => (
                      <span key={index} className="text-secondary-light">
                        {slot.date}, {slot.start_time} - {slot.end_time}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {!booking.needs_emergency_service &&
                booking.agreed_services &&
                booking.agreed_services.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Agreed Services
                    </label>
                    <div className="table-responsive">
                      <table className="table table-sm bordered-table mb-2">
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Parts (Min - Max)</th>
                            <th>Labour</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {booking.agreed_services.map((item, index) => (
                            <tr key={index}>
                              <td>
                                {item.service_name || `#${item.service_id}`}
                              </td>
                              <td>
                                {item.parts_min} - {item.parts_max}
                              </td>
                              <td>{item.labour}</td>
                              <td className="text-capitalize">
                                {(item.service_status || "not_started").replace(
                                  "_",
                                  " ",
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="form-control-static fw-semibold">
                      Total Range: {booking.price_min} - {booking.price_max}
                    </p>
                  </div>
                )}

              {booking.additional_services &&
                booking.additional_services.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Additional Services Proposed
                    </label>
                    <div className="table-responsive">
                      <table className="table table-sm bordered-table mb-2">
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Parts (Min - Max)</th>
                            <th>Labour</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {booking.additional_services.map((item) => (
                            <tr key={item.proposal_id}>
                              <td>
                                {item.service_name || `#${item.service_id}`}
                              </td>
                              <td>
                                {item.parts_min} - {item.parts_max}
                              </td>
                              <td>{item.labour}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    item.status === "accepted"
                                      ? "bg-success"
                                      : item.status === "rejected"
                                        ? "bg-danger"
                                        : "bg-warning"
                                  } text-capitalize`}
                                >
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {booking.additional_services_min != null && (
                      <p className="form-control-static fw-semibold">
                        Pending Additional Range:{" "}
                        {booking.additional_services_min} -{" "}
                        {booking.additional_services_max}
                      </p>
                    )}
                  </div>
                )}

              {booking.garage_offered_slots?.length > 0 && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Garage Offered Slots
                  </label>
                  <div className="d-flex flex-column gap-1">
                    {booking.garage_offered_slots.map((slot, index) => (
                      <span key={index} className="text-secondary-light">
                        {slot.date}, {slot.time}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {booking.garage_response_note && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Garage Response Note
                  </label>
                  <p className="form-control-static">
                    {booking.garage_response_note}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        {(booking.rejection_reason || booking.additional_note) && (
          <div className="card h-100 p-0 radius-12 my-3">
            <div className="card-header bg-base py-3">
              <h6 className="text-lg fw-semibold mb-0">Reasons & Notes</h6>
            </div>
            <div className="card-body p-24">
              {booking.rejection_reason && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Rejection Reason
                  </label>
                  <div className=" mb-0" role="alert">
                    {booking.rejection_reason}
                  </div>
                </div>
              )}

              {booking.additional_note && (
                <div className="mb-0">
                  <label className="form-label fw-semibold">
                    Additional Note
                  </label>
                  <div className="mb-0" role="alert">
                    {booking.additional_note}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="card h-100 p-0 radius-12 my-3">
          <div className="card-header bg-base py-3">
            <h6 className="text-lg fw-semibold mb-0">Gallery</h6>
          </div>

          <div className="card-body p-24">
            {booking.gallery_urls?.length > 0 ? (
              <div className="d-flex flex-wrap gap-3">
                {booking.gallery_urls.map((imageUrl, index) => (
                  <div
                    key={index}
                    className="border rounded overflow-hidden"
                    style={{ width: "140px", height: "140px" }}
                  >
                    <img
                      src={imageUrl}
                      alt={`Booking ${index + 1}`}
                      className="w-100 h-100"
                      style={{
                        objectFit: "cover",
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedImageIndex(index)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">No images available</p>
            )}
          </div>

          {/* Image Preview Modal */}
          {selectedImageIndex !== null && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                zIndex: 9999,
              }}
            >
              {/* Previous Image */}
              <button
                type="button"
                className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                disabled={selectedImageIndex === 0}
                style={{
                  position: "absolute",
                  left: "30px",
                  width: "45px",
                  height: "45px",
                  zIndex: 10000,
                  opacity: selectedImageIndex === 0 ? 0.5 : 1,
                  cursor: selectedImageIndex === 0 ? "not-allowed" : "pointer",
                }}
                onClick={handlePreviousImage}
              >
                <i className="ri-arrow-left-s-line fs-4" />
              </button>

              {/* Preview Image */}
              <img
                src={booking.gallery_urls[selectedImageIndex]}
                alt={`Booking ${selectedImageIndex + 1}`}
                style={{
                  maxWidth: "85%",
                  maxHeight: "85%",
                  objectFit: "contain",
                }}
              />

              {/* Next Image */}
              <button
                type="button"
                className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                disabled={
                  selectedImageIndex === booking.gallery_urls.length - 1
                }
                style={{
                  position: "absolute",
                  right: "30px",
                  width: "45px",
                  height: "45px",
                  zIndex: 10000,
                  opacity:
                    selectedImageIndex === booking.gallery_urls.length - 1
                      ? 0.5
                      : 1,
                  cursor:
                    selectedImageIndex === booking.gallery_urls.length - 1
                      ? "not-allowed"
                      : "pointer",
                }}
                onClick={handleNextImage}
              >
                <i className="ri-arrow-right-s-line fs-4" />
              </button>

              {/* Close Button */}
              <button
                type="button"
                className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  position: "absolute",
                  top: "25px",
                  right: "25px",
                  width: "40px",
                  height: "40px",
                  zIndex: 10000,
                }}
                onClick={() => setSelectedImageIndex(null)}
              >
                <i className="ri-close-line fs-4" />
              </button>

              {/* Image Counter */}
              <div
                className="text-white"
                style={{
                  position: "absolute",
                  bottom: "25px",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                {selectedImageIndex + 1} / {booking.gallery_urls.length}
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ flex: "1", minWidth: 0 }}>
        <div className="card h-100 p-0 radius-12 my-3">
          <div className="card-header bg-base py-3">
            <h6 className="text-lg fw-semibold mb-0">Customer Information</h6>
          </div>

          <div className="card-body p-24">
            <div className="d-flex align-items-center mb-3">
              <img
                src={
                  booking.user.image ||
                  "/assets/images/user-list/user-list1.png"
                }
                alt="Customer"
                className="w-60-px h-60-px rounded-circle flex-shrink-0 me-3 overflow-hidden"
              />

              <div>
                <h5 className="mb-1">{booking.user.fullName}</h5>
                <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
                  @{booking.user.username}
                </p>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Email
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px", overflowWrap: "anywhere" }}
                >
                  {booking.user.email || "Not provided"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Phone Number
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.user.phoneNumber || "Not provided"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Status
                </label>
                <p className="form-control-static mb-0">
                  <span
                    className="badge"
                    style={{
                      backgroundColor: booking.user.isActive
                        ? "#B9F2CE"
                        : "#E2E5E9",
                      color: booking.user.isActive ? "#176638" : "#454B54",
                      fontSize: "12px",
                    }}
                  >
                    {booking.user.isActive ? "Active" : "Inactive"}
                  </span>
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Member Since
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.user.createdAt
                    ? format(new Date(booking.user.createdAt), "dd MMM, yyyy")
                    : "Not available"}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card h-100 p-0 radius-12 my-3">
          <div className="card-header bg-base py-3">
            <h6 className="text-lg fw-semibold mb-0">Vehicle Information</h6>
          </div>

          <div className="card-body p-24">
            <div className="row g-3">
              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Model
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.vehicle.model || "Not specified"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Year
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.vehicle.year || "Not specified"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  License Plate
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.vehicle.licensePlate || "Not specified"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Fuel Type
                </label>
                <p
                  className="form-control-static text-capitalize mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.vehicle.fuelType || "Not specified"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Transmission
                </label>
                <p
                  className="form-control-static text-capitalize mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.vehicle.transmissionType || "Not specified"}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Date Added
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.vehicle.createdAt
                    ? format(
                        new Date(booking.vehicle.createdAt),
                        "dd MMM, yyyy",
                      )
                    : "Not available"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card h-100 p-0 radius-12 my-3">
          <div className="card-header bg-base py-3">
            <h6 className="text-lg fw-semibold mb-0">Garage Information</h6>
          </div>

          <div className="card-body p-24">
            <div className="row g-3">
              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Garage Name
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.garage.garage_name}
                </p>
              </div>

              <div className="col-6">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Registration Number
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.garage.business_registration_number}
                </p>
              </div>

              <div className="col-12">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Business Address
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.garage.address}
                </p>
              </div>

              <div className="col-12">
                <label
                  className="form-label mb-0 text-secondary"
                  style={{ fontSize: "12px" }}
                >
                  Coordinates
                </label>
                <p
                  className="form-control-static mb-0"
                  style={{ fontSize: "14px" }}
                >
                  {booking.garage.latitude != null &&
                  booking.garage.longitude != null
                    ? `${Number(booking.garage.latitude).toFixed(6)}, ${Number(
                        booking.garage.longitude,
                      ).toFixed(6)}`
                    : "Not available"}
                </p>
              </div>

              {booking.garage.latitude != null &&
                booking.garage.longitude != null && (
                  <div className="col-12 mt-2">
                    <label
                      className="form-label mb-2 text-secondary"
                      style={{ fontSize: "12px" }}
                    >
                      Location Map
                    </label>

                    <MapContainer
                      center={[
                        parseFloat(booking.garage.latitude),
                        parseFloat(booking.garage.longitude),
                      ]}
                      zoom={13}
                      style={{
                        height: "300px",
                        width: "100%",
                        borderRadius: "8px",
                        border: "none",
                        outline: "none",
                        backgroundColor: "#fff",
                      }}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker
                        position={[
                          parseFloat(booking.garage.latitude),
                          parseFloat(booking.garage.longitude),
                        ]}
                      />
                    </MapContainer>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBookingLayer;
